import React, { useEffect, useRef, useState } from "react";
import api from "../../api/api";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight } from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const pageRef = useRef(null);

  const load = async () => {
    const res = await api.get("/posts?status=published&limit=50");
    setPosts(res.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
        ease: "power3.out",
      });

      gsap.from(".blog-card", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".blog-grid",
          start: "top 85%",
        },
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef}>
      <section className="relative bg-white pb-24 overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-52 md:pb-60 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AKSUBEB News and Blog"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />

          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#0b6b1d]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#0b6b1d]/35 blur-3xl" />

          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />

              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Latest Updates
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              News and Blog
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Latest updates from Akwa Ibom State Universal Education Board.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>

              <ChevronRight size={14} />

              <span className="text-[#ff6600]">News and Blog</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 -mt-36 md:-mt-44 z-10">
          {posts.length > 0 ? (
            <div className="blog-grid grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="blog-card bg-white rounded-3xl overflow-hidden shadow-[0_18px_45px_rgba(0,0,0,0.12)] border border-slate-100"
                >
                  <div className="relative h-60 overflow-hidden">
                    <img
                      src={post.featured_image || "/assets/images/children.webp"}
                      alt={post.title}
                      className="w-full h-full object-cover transition-all duration-700 hover:scale-110"
                    />
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <Calendar size={15} />
                      {post.published_at?.slice(0, 10)}
                    </div>

                    <h2
                      className="text-xl font-black text-[#0b6b1d] leading-tight"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {post.title}
                    </h2>

                    <p className="mt-3 text-slate-600 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>

                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-block mt-5 font-black text-[#ff6600] hover:text-[#0b6b1d] transition-colors"
                    >
                      Read More
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-10 text-center shadow-sm border border-slate-100">
              <h2 className="text-2xl font-black text-[#0b6b1d]">
                No Published Posts Yet
              </h2>

              <p className="mt-3 text-slate-600">
                Published news and blog posts will appear here.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default BlogPage;